/**
 * Wire-shape constants shared across the frontend.
 *
 * Keep this file in sync with the Go producer referenced in each
 * constant's JSDoc. Cross-language drift is the primary risk — the
 * companion regression anchor in `MessageList.test.tsx` hardcodes the
 * byte-for-byte literal and must be updated together with this file if
 * the producer format changes.
 */

/**
 * Prefix that api-service-go's intent dispatcher prepends to every
 * synthetic user message it injects into the chat history when a UI
 * action button is clicked.
 *
 * Producer: `api-service-go/uiactions/intent_dispatcher.go`
 * Function: `SyntheticIntentQuery`
 * Format:   `[card-action] intent=%q params=%s source=%q`
 *
 * The frontend filters these messages out of the visible message list so
 * the nurse never sees the raw machine-readable envelope. If the producer
 * changes the prefix (rename, version tag, trailing space dropped), this
 * constant AND the filter in `MessageList.tsx` break together — that is
 * the intended coupling. The companion literal in `MessageList.test.tsx`
 * is the byte-for-byte regression anchor.
 *
 * Follow-up: add a wire-shape fixture that both
 * `intent_dispatcher_test.go` and a TS contract test assert against,
 * so cross-language drift is caught in CI rather than at runtime.
 */
export const CARD_ACTION_MESSAGE_PREFIX = '[card-action] '

/**
 * Prefix tagging the synthetic user-role bootstrap note api-service-go writes
 * into the twin's welcome session right after onboarding completes (see
 * design doc "agent onboarding", LOCKED decision 1). The note is the trigger
 * for the agent-initiated welcome turn — its content is an English data-note
 * for the LLM ("user just completed onboarding..."), not something the nurse
 * should ever read.
 *
 * Producer: `api-service-go` onboarding handler (role="user", AuthorID="").
 * Source of truth: `tricorder-go` `AgentOpeningNotePrefix`
 * Format:   `[onboarding_welcome] <English data-note>`
 *
 * The frontend filters these out of the visible message list — same coupling
 * pattern as CARD_ACTION_MESSAGE_PREFIX above: producer change breaks both
 * literals together.
 */
export const AGENT_OPENING_NOTE_PREFIX = '[onboarding_welcome] '

/**
 * Prefix tagging the user-role message an AGENT writes into a child session
 * when it delegates a question through `ask_agent` (api-service-go's
 * `HandleSpawnChild`, prompt branch).
 *
 * Producer: `api-service-go` delegation path.
 * Source of truth: `tricorder-go` `chat.DelegatedPromptPrefix`
 * Format:   `[delegated agent="<slug>"] <the question, in the user's language>`
 *
 * Unlike CARD_ACTION_MESSAGE_PREFIX and AGENT_OPENING_NOTE_PREFIX, this
 * message is NOT hidden. Those two carry
 * machine text; this one carries the real clinical question one agent put to
 * another, and the nurse who opens the consult has to see it — otherwise she
 * reads an answer to a question that appears nowhere. The marker is stripped
 * and the message renders normally.
 */
export const DELEGATED_PROMPT_PREFIX = '[delegated '

/**
 * Prefix tagging the user-role message that carries a delegated agent's ANSWER
 * back into the session that asked (api-service-go's `returnDelegatedAnswer`,
 * enqueued when the child's delegated turn completes).
 *
 * Producer: `api-service-go` turn worker.
 * Source of truth: `tricorder-go` `chat.DelegationReplyPrefix`
 * Format:   `[delegation_reply agent="<slug>" session="<id>"] <answer>`
 *
 * Visible for the same reason as DELEGATED_PROMPT_PREFIX above: the twin's next
 * reply refers to this consult, so hiding it would leave the nurse reading a
 * conclusion drawn from something she never saw.
 */
export const DELEGATION_REPLY_PREFIX = '[delegation_reply '

/**
 * The turn-failure reason for a spent model quota (api-service-go
 * session.TurnFailQuotaExceeded). Its own message, because unlike a generic
 * failure it tells the reader what to do: wait out the minute, and raise the
 * quota if it keeps happening.
 */
export const TURN_FAIL_QUOTA_EXCEEDED = 'model quota exceeded'
